import {applyOperation} from '@github-ui/optimistic-connection'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useQueryClient} from '@github-ui/react-query'
import type {CreateRepositoryLabelMutationMutation} from '@github-ui/server-persisted-queries/types/createRepositoryLabelMutation'
import {useCallback, useRef} from 'react'

import {LABELS} from './constants/labels'
import type {LabelsQueryData} from './types'
import {
  buildConfirmedCreateLabelOperation,
  buildCreateLabelOperation,
  type LabelInput,
  labelInputFromOperation,
  LABELS_CONNECTION_PATH,
  newTempLabelId,
} from './utils/label-connection-operations'
import {createLabelRequest} from './utils/label-requests'

type UseCreateLabelOptions = {
  repositoryId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setSubmissionErrors: (v: string) => void
  setDialogOpen: (open: boolean) => void
  onSuccess?: () => void
}

type CreateLabelHandler = (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => void

export function useCreateLabel({
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  setSubmissionErrors,
  setDialogOpen,
  onSuccess,
}: UseCreateLabelOptions): CreateLabelHandler {
  const queryClient = useQueryClient()
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})

  const {mutate} = useConnectionMutation<CreateRepositoryLabelMutationMutation, Error>({
    ownerId: repositoryId,
    connectionPath: LABELS_CONNECTION_PATH,
    // The operation's node carries the raw input the write needs.
    mutationFn: async operation => {
      const input = labelInputFromOperation(operation)
      if (!input) throw new Error(LABELS.labelCreateError)
      return createLabelRequest(repositoryNameWithOwner, repositoryId, input)
    },
    onError: error => {
      setSubmissionErrors(error.message)
      setIsSubmittingRef.current(false)
      setDialogOpen(true)
    },
    onSuccess: (data, {operation}) => {
      const createdLabel = data.createLabel?.label
      const tempId = operation.type === 'addToConnection' ? operation.node.id : undefined
      if (queryKey && createdLabel && tempId) {
        queryClient.setQueryData<LabelsQueryData>(queryKey, base =>
          base ? applyOperation(base, repositoryId, buildConfirmedCreateLabelOperation(tempId, createdLabel)) : base,
        )
      }
      onSuccess?.()
    },
  })

  return useCallback(
    (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      setDialogOpen(false)
      mutate({operation: buildCreateLabelOperation(newTempLabelId(), input)})
    },
    [mutate, setDialogOpen],
  )
}
